/**
 * Brief pace × a product's card-details pace (XT-806).
 *
 * The request's pace (relaxed / balanced / active) and an experience's card
 * pace (Relaxed / Balanced / Active, `card_facts.pace`) use the same words.
 * Automatic picks (day Suggestions, trip Generate) use it twice:
 * - a gate: a relaxed party is never offered an Active experience;
 * - an order factor: the closer the pace, the earlier the product.
 *
 * A product with no card pace is never gated and never reordered by it — most
 * of the catalogue has none yet. The agent's own browsing is not gated here
 * (TAP01-050 only lists the brief's pace first).
 */

import type { CardPace } from '@travel-planner/shared/schemas/card-facts';
import type { DayPace } from './day-catalog';

/** `packed` (legacy density word) is the same party as `active`. */
const briefPaceOf = (
  pace: DayPace | null | undefined
): 'relaxed' | 'balanced' | 'active' | null => {
  if (!pace) return null;
  return pace === 'packed' ? 'active' : pace;
};

/** False only when a relaxed party meets an Active experience. */
export const paceAllows = (
  pace: DayPace | null | undefined,
  cardPace: CardPace | null | undefined
): boolean => !(briefPaceOf(pace) === 'relaxed' && cardPace === 'Active');

const FACTOR: Record<
  'relaxed' | 'balanced' | 'active',
  Record<CardPace, number>
> = {
  // Active never reaches the factor for a relaxed party (gated).
  relaxed: { Relaxed: 1.1, Balanced: 1, Active: 1 },
  balanced: { Relaxed: 0.9, Balanced: 1.1, Active: 0.9 },
  active: { Relaxed: 0.85, Balanced: 1, Active: 1.25 },
};

/** Order factor for a product whose card pace is set; 1 when either is unset. */
export const cardPaceFactor = (
  pace: DayPace | null | undefined,
  cardPace: CardPace | null | undefined
): number => {
  const brief = briefPaceOf(pace);
  if (!brief || !cardPace) return 1;
  return FACTOR[brief][cardPace];
};
